/* ============================================================
   KOTAK "BEG KUNING" (TikTok) + "LINK PRODUK" (Facebook) — Control Panel v3.9
   (2026-09-18, rupa diluluskan user selepas lakaran)

   RUPA SAHAJA. Nilai, simpanan dan peraturan saling-menolak hidup dalam
   App.jsx (`begKuningAktif`/`tukarBegKuning`, `fbAddProductAktif`/
   `tukarFbAddProduct`, `tukarPostOnly`, `tukarShowcase`, `ubahCtaPilihan`).
   Baris suis dilukis oleh `barisSuis` = pembantu `barisSuisV39` App.jsx,
   supaya rupa suis kekal SATU takrifan. Laluan larian: utils/pautanProduk.js.

   Susunan kotak TikTok (keputusan user 18 Sept): toggle → dropdown CTA beg
   kuning → Post Mode (Post Only, Showcase Only). Dropdown + Post Mode HANYA
   dipapar bila beg kuning HIDUP — kedua-dua mod memang keluarga "produk", dan
   CTA ialah teks pada beg itu sendiri.

   Kotak Facebook (2026-09-23): toggle → dropdown CTA yang SAMA. Sebabnya CTA
   memang sudah dihantar ke robot Facebook sejak v4 (ia menjadi "Link name" pada
   pautan produk reel) — cuma pelanggan tidak pernah nampak pilihannya di sini,
   jadi reel diam-diam memakai pilihan TikTok atau lalai 'BELI KAT SINI'.
   ============================================================ */

import { useState } from 'react';

import { useCiriPercuma } from '../hooks/useCiriPercuma';
import PopupCiriPercuma from './PopupCiriPercuma';
import { CIRI, bolehGunaCiri } from '../utils/ciriPercuma';

const GAYA_KOTAK = {
    display: 'flex', flexDirection: 'column', gap: 12,
    padding: '12px 14px', marginBottom: 16,
    border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r)',
};

const GAYA_PILIH = {
    width: '100%', boxSizing: 'border-box', height: 34, padding: '0 8px',
    borderRadius: 8, border: '1px solid var(--s-line-strong)',
    background: 'var(--s-fill)', color: 'var(--s-ink)', font: 'inherit', fontSize: 13,
};

/* Dropdown CTA — SATU takrifan, dipakai oleh kotak TikTok DAN kotak Facebook.
   ⛔ SATU pilihan untuk kedua-dua platform (keputusan user 2026-09-23): TikTok
   memakainya sebagai teks butang beg kuning, Facebook sebagai "Link name" pada
   pautan produk reel (ayat yang audiens baca). Menukar di satu tempat menukar
   kedua-duanya — itu memang perangai yang sudah berjalan sejak CTA disambung ke
   Facebook; sebelum ini ia cuma TIDAK KELIHATAN bila Facebook dipilih.
   `hint` berbeza mengikut platform kerana ayatnya muncul di tempat berbeza. */
/* 🔒 [2026-09-26] CTA sendiri = ciri PRO Studio, WAJIB claim percuma (hingga
   30 Nov 2026, keputusan user). Belum claim / tamat: hanya lalai "BELI KAT SINI"
   boleh dipilih; CTA sendiri dipapar tetapi dikunci. Pilihan tersimpan TIDAK
   ditimpa — selepas claim, pilihan lama pelanggan kembali.
   ⚠️ KEMBAR: `ctaTeksLarian` dalam App.jsx (teks sebenar ke robot) guna
   syarat yang SAMA — kunci di sini tanpa di sana = dropdown menipu. */
function PilihCtaV39({ t, cta, hint }) {
    const { pilihan, senarai, sudahMuat, onUbah } = cta;
    const ciriP = useCiriPercuma(true);
    const statusCta = ciriP.status(CIRI.CTA);
    const kunci = ciriP.sudahMuat && !bolehGunaCiri(statusCta);
    const [popup, setPopup] = useState(false);
    /* Pilihan tergantung: template yang dipilih sudah dipadam di PRO Studio.
       Larian jatuh ke lalai — beritahu, jangan biar ia berlaku senyap. */
    const ctaHilang = !kunci && !!pilihan && sudahMuat && !senarai.some((c) => c.id === pilihan);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select
                value={kunci ? '' : pilihan}
                onChange={(e) => { if (!kunci) onUbah(e.target.value); }}
                aria-label={t('workspace.cta.label', 'CTA beg kuning')}
                style={GAYA_PILIH}
            >
                <option value="">{t('workspace.cta.default', 'Lalai — BELI KAT SINI')}</option>
                {senarai.map((c) => (
                    <option key={c.id} value={c.id} disabled={kunci}>
                        {kunci ? `${c.teks} · ${t('workspace.cta.lockedTag', 'dikunci')}` : c.teks}
                    </option>
                ))}
            </select>
            {kunci && (
                <p className="s-hint" style={{ margin: 0 }}>
                    {statusCta === 'tamat'
                        ? t('workspace.cta.lockedTamat', 'Tempoh percuma CTA sendiri telah tamat. Lalai "BELI KAT SINI" digunakan.')
                        : (
                            <>
                                {t('workspace.cta.lockedBelum', 'CTA anda sendiri dikunci. Lalai "BELI KAT SINI" digunakan.')}{' '}
                                <button type="button" onClick={() => setPopup(true)} style={{
                                    border: 0, padding: 0, background: 'none', font: 'inherit', cursor: 'pointer',
                                    color: 'var(--s-ink)', textDecoration: 'underline',
                                }}>
                                    {t('workspace.cta.activate', 'Aktifkan CTA percuma hingga 30 Nov 2026')}
                                </button>
                            </>
                        )}
                </p>
            )}
            {popup && <PopupCiriPercuma ciri={CIRI.CTA} onTutup={() => setPopup(false)} t={t} />}
            {ctaHilang && (
                <p className="s-hint" style={{ margin: 0, color: 'var(--s-warn)' }}>
                    {t('workspace.cta.missing', 'CTA yang anda pilih sudah tiada. Lalai digunakan.')}
                </p>
            )}
            <p className="s-hint" style={{ margin: 0 }}>{hint}</p>
        </div>
    );
}

/* cta      = { pilihan, senarai, sudahMuat, onUbah }  (CTA beg kuning, per-browser)
   postMode = { postOnly, onPostOnly, showcaseOnly, onShowcaseOnly } */
export function KotakBegKuningV39({ t, barisSuis, aktif, onTukar, cta, postMode }) {
    return (
        <div style={GAYA_KOTAK}>
            <p className="s-sect__title">{t('workspace.bag.title', 'Beg kuning')}</p>
            {barisSuis({
                kunci: 'begkuning', label: t('workspace.bag.toggle', 'Pasang beg kuning'),
                nota: aktif
                    ? t('workspace.bag.noteOn', 'Robot pautkan produk kad pada setiap video (beg kuning TikTok Shop).')
                    : t('workspace.bag.noteOff', 'Video dipost tanpa beg kuning — tiada langkah Showcase, tiada pautan produk.'),
                on: aktif, onChange: onTukar,
            })}
            {aktif && (
                <>
                    <PilihCtaV39
                        t={t}
                        cta={cta}
                        hint={t('workspace.cta.hint', 'Teks butang pada pautan produk. Disimpan pada browser ini sahaja.')}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 15, paddingTop: 12, borderTop: '1px solid var(--s-line)' }}>
                        <p className="s-sect__title">{t('workspace.page.sect.postMode', 'Post Mode')}</p>
                        {barisSuis({
                            kunci: 'postonly', label: t('workspace.page.post.postOnly', 'Post Only'),
                            nota: t('workspace.post.postOnlyNote', 'Langkau langkah masukkan produk ke Showcase. Beg kuning tetap dipasang kalau produk sudah ada dalam Showcase anda.'),
                            on: postMode.postOnly, onChange: postMode.onPostOnly,
                        })}
                        {barisSuis({
                            kunci: 'showcase', label: t('workspace.page.post.showcaseOnly', 'Showcase Only'),
                            nota: t('workspace.post.showcaseNote', 'Masukkan produk ke Showcase tanpa memuat naik video.'),
                            on: postMode.showcaseOnly, onChange: postMode.onShowcaseOnly,
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

/* cta = { pilihan, senarai, sudahMuat, onUbah } — SAMA objek dengan kotak TikTok.
   Dropdown hanya dipapar bila Add product HIDUP (link mati → tiada ayat untuk
   dipilih). `cta` dijaga kalau-kalau skrin lain memanggil kotak ini tanpa CTA. */
export function KotakAddProductFbV39({ t, barisSuis, aktif, onTukar, cta }) {
    return (
        <div style={GAYA_KOTAK}>
            <p className="s-sect__title">{t('workspace.fbproduk.title', 'Link produk')}</p>
            {barisSuis({
                kunci: 'fbaddproduct', label: t('workspace.fbproduk.toggle', 'Add product'),
                nota: aktif
                    ? t('workspace.fbproduk.noteOn', 'Robot letak link Shopee kad pada setiap reel (Facebook: Add product). Reel dapat label "Commission eligible".')
                    : t('workspace.fbproduk.noteOff', 'Reel dipost tanpa link produk — tiada label "Commission eligible". Link Shopee pada kad kekal, cuma tidak digunakan.'),
                on: aktif, onChange: onTukar,
            })}
            {aktif && cta && (
                <PilihCtaV39
                    t={t}
                    cta={cta}
                    hint={t('workspace.cta.hintFb', 'Ayat pada pautan produk reel (Facebook: "Link name"). Sama dengan CTA beg kuning TikTok — satu pilihan untuk kedua-dua. Disimpan pada browser ini sahaja.')}
                />
            )}
        </div>
    );
}
